'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
var Currency = require('../../transformers/Currency.js');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var React__default = /*#__PURE__*/_interopDefaultLegacy(React);

const DEFAULT_ITEM_ATTR_CONFIG = ['quantity', 'variantInfo', 'color', 'size', 'sku', 'price'];
const filterItems = function (items) {
  let submittedReturns = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  let showOfflineReturnsAs = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'hidden';
  const eligible = [];
  const ineligible = [];
  const hasOfflineReturnItem = item => {
    const onlineReturnedQuantity = submittedReturns.map(r => r.returnShipments ?? []).flat().map(s => s.items).flat().reduce((sum, submittedReturnItem) => submittedReturnItem.itemId === item.itemId ? sum + submittedReturnItem.quantity : sum, 0);
    return item.orderedQuantity - onlineReturnedQuantity > item.remainingQuantity;
  };
  items.forEach(item => {
    var _eligibility$return, _eligibility$exchange;
    const eligibility = item.eligibilities;
    if ((eligibility !== null && eligibility !== void 0 && (_eligibility$return = eligibility.return) !== null && _eligibility$return !== void 0 && _eligibility$return.isEligible || eligibility !== null && eligibility !== void 0 && (_eligibility$exchange = eligibility.exchange) !== null && _eligibility$exchange !== void 0 && _eligibility$exchange.isEligible) && item.remainingQuantity > 0) {
      eligible.push(item);
    } else if (item.remainingQuantity > 0 || showOfflineReturnsAs === 'ineligible' && hasOfflineReturnItem(item)) {
      ineligible.push(item);
    }
  });
  return [eligible, ineligible];
};
const splitItemsForMixReturns = (items, selectedItems) => {
  const splitItems = (items ?? []).reduce((ary, item) => {
    // split orderItems into multiple rows base on current selections
    let split = (selectedItems ?? []).filter(itemInput => itemInput.itemId === item.itemId && itemInput.sku === item.sku).map(itemInput => ({
      ...item,
      localId: itemInput.localId,
      remainingQuantity: itemInput.quantity
    }));

    // max localId of current selected item, return -1 if it was unselected
    const maxLocalId = Math.max(-1, ...split.map(item => item.localId ?? 0));
    const selectedQty = split.reduce((sum, item) => sum + item.remainingQuantity, 0);
    const totalQty = item.remainingQuantity;
    const remainingQuantity = totalQty - selectedQty;

    // append another row if there is still any remaining quantity.
    if (remainingQuantity > 0) {
      split = [...split, {
        ...item,
        localId: maxLocalId + 1,
        // last remaining item's localId is always max ID+1, so even if a selected item was removed, the ID won't mixup.
        remainingQuantity
      }];
    }
    return [...ary, ...split];
  }, []);
  return splitItems;
};
const getAdditionalItemAttrs = additionalProperties => {
  if (!additionalProperties) {
    return null;
  }

  /**
   * Additional Properties come in a different format than what we need for getItemAttrs.
   * We convert these to an object of objects, formatting key, and label.
   */
  return additionalProperties.reduce((acc, attr) => {
    const {
      name,
      value
    } = attr;

    // First we split the name into an array
    const attrNameArray = name.split('_');

    // Name attr is formatted to a label.  e.g. `Rx_number` to `Rx number`
    const joinedName = attrNameArray.join(' ');
    const label = `${joinedName.charAt(0).toUpperCase()}${joinedName.slice(1)}`;

    // A key is formatted to snake case. e.g. `Rx_number` to `rxNumber`
    const key = attrNameArray.length === 1 ? `${name.charAt(0).toLowerCase()}${name.slice(1)}` : attrNameArray.map((word, i) => {
      let str = word.toLowerCase();
      if (i !== 0) {
        str = `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
      }
      return str;
    }).join('');
    acc[key] = {
      label,
      value
    };
    return acc;
  }, {});
};
const getTransformedAttribute = (transformType, _ref) => {
  var _additionalItemAttrs$;
  let {
    key,
    additionalItemAttrs,
    additionalItemAttributeConfig,
    currency,
    includeCurrency,
    locale
  } = _ref;
  const value = additionalItemAttrs === null || additionalItemAttrs === void 0 ? void 0 : (_additionalItemAttrs$ = additionalItemAttrs[key]) === null || _additionalItemAttrs$ === void 0 ? void 0 : _additionalItemAttrs$.value;
  if (!value) {
    return '';
  }

  // If there is no transform, we return the value as is.
  if (transformType === 'none') {
    return value;
  }

  // If thee transform is to concatenate, we need to find the additional attribute config for the concat key,
  // then concat it as a prefix or suffix. Usually, the concatenated attribute will be excluded from display in itemAttributeConfig.
  if (transformType === 'toConcatenate') {
    var _concatenatedAttribut;
    if (!additionalItemAttributeConfig) {
      return value;
    }
    const thisAdditionalItemAttributeConfig = additionalItemAttributeConfig === null || additionalItemAttributeConfig === void 0 ? void 0 : additionalItemAttributeConfig.find(item => item.attribute === key);
    const thisProperty = thisAdditionalItemAttributeConfig === null || thisAdditionalItemAttributeConfig === void 0 ? void 0 : thisAdditionalItemAttributeConfig.value;
    const concatenatedAttributeConfig = additionalItemAttributeConfig === null || additionalItemAttributeConfig === void 0 ? void 0 : additionalItemAttributeConfig.find(item => item.attribute === (thisProperty === null || thisProperty === void 0 ? void 0 : thisProperty.concatKey));
    const transformedConcatValue = getTransformedAttribute((concatenatedAttributeConfig === null || concatenatedAttributeConfig === void 0 ? void 0 : (_concatenatedAttribut = concatenatedAttributeConfig.value) === null || _concatenatedAttribut === void 0 ? void 0 : _concatenatedAttribut.transform) ?? 'none', {
      key: (thisProperty === null || thisProperty === void 0 ? void 0 : thisProperty.concatKey) ?? '',
      additionalItemAttrs,
      additionalItemAttributeConfig,
      currency,
      locale
    });
    return (thisProperty === null || thisProperty === void 0 ? void 0 : thisProperty.concatPosition) === 'suffix' ? `${value}${transformedConcatValue}` : `${transformedConcatValue}${value}`;
  }

  // If the transform is to currency, we use the Intl.NumberFormat to format the value as currency.
  if (transformType === 'toCurrency') {
    return Currency.formatCurrency({
      value: parseFloat(value),
      currency,
      includeCurrency,
      locale,
      needsExponent: !(value !== null && value !== void 0 && value.toString().includes('.'))
    });
  }

  // If the transform is to lowercase, we return the value in lowercase.
  if (transformType === 'toLowercase') {
    return value.toLowerCase();
  }

  // Otherwise, we simply return the value as is.
  return value;
};
const getItemAttrs = _ref2 => {
  let {
    item,
    itemDisplay,
    selectedQuantity,
    labels,
    exchangeTo,
    locale,
    includeCurrency
  } = _ref2;
  const {
    additionalItemAttributeConfig,
    itemAttributeConfig
  } = itemDisplay || {};

  /**
   * In order to order and potential hide any attributes, we leverage a config array that
   * includes allowed keys in a desired order.
   *
   * itemAttributeConfig - A prop that comes from itemDisplay via useSettings() and is set in Quin retailer settings for returns.
   * In the absence of itemAttributeConfig, we use a default config DEFAULT_ITEM_ATTR_CONFIG.
   */
  const ITEM_ATTR_CONFIG = itemAttributeConfig ?? DEFAULT_ITEM_ATTR_CONFIG;
  const variantInfo = 'variantInfo' in item && item.variantInfo;
  const hasVariantInfo = !!variantInfo;

  // We also need to set up a config array for the variant attributes as they have their own ordering logic.
  const VARIANT_ATTR_CONFIG = hasVariantInfo ? Object.entries(variantInfo)
  // First sort the variant entries
  .sort((a, b) => {
    const entriesLength = Object.entries(variantInfo).length;
    return (a[1].optionPosition ?? entriesLength) - (b[1].optionPosition ?? entriesLength);
  })
  // Next reduce the entries to an array of keys.
  .reduce((acc, variantEntry) => {
    acc.push(variantEntry[0]);
    return acc;
  }, []) : [];

  // We want to avoid the duplication of variant keys in the ITEM_ATTR_CONFIG.
  // Variant keys trump ITEM_ATTR_CONFIG keys, so we throw away duplicates from ITEM_ATTR_CONFIG.
  const REDUCED_ITEM_ATTR_CONFIG = ITEM_ATTR_CONFIG.reduce((acc, key) => {
    if (key && !VARIANT_ATTR_CONFIG.includes(key)) {
      acc.push(key);
    }
    return acc;
  }, []);

  // Next we need to produce a final DISPLAY_ITEM_ATTR_CONFIG, spreading the VARIANT_ATTR_CONFIG into
  // the index of variantInfo in the REDUCED_ITEM_ATTR_CONFIG.
  const variantInfoIndex = REDUCED_ITEM_ATTR_CONFIG.indexOf('variantInfo');
  const DISPLAY_ITEM_ATTR_CONFIG = variantInfoIndex >= 0 ? [...REDUCED_ITEM_ATTR_CONFIG.slice(0, variantInfoIndex), ...VARIANT_ATTR_CONFIG, ...REDUCED_ITEM_ATTR_CONFIG.slice(variantInfoIndex + 1)] : [...REDUCED_ITEM_ATTR_CONFIG];

  // Finally, we begin the collection of attributes for display, by first producing slots
  // for each attribute to be placed. Empty slots will be removed at the end.
  const attributes = Array(DISPLAY_ITEM_ATTR_CONFIG.length);

  // QUANTITY
  const displayQuantity = selectedQuantity ? selectedQuantity : item.quantity;
  const hasQuantity = displayQuantity > 0;
  const quantityIndex = DISPLAY_ITEM_ATTR_CONFIG.indexOf('quantity');
  const includeQuantity = hasQuantity && quantityIndex >= 0;
  if (includeQuantity) {
    attributes[quantityIndex] = {
      key: labels === null || labels === void 0 ? void 0 : labels.qtyLabel,
      value: displayQuantity
    };
  }

  // VARIANT INFO
  // These booleans are used to track whether color or size are included via variant info.
  let colorIncludedInVariant = false;
  let sizeIncludedInVariant = false;

  // When checking whether to include the variantInfo, we need to check the REDUCED_ITEM_ATTR_CONFIG, and
  // not the DISPLAY_ITEM_ATTR_CONFIG, as the variantInfo has already been merged in
  if (hasVariantInfo && REDUCED_ITEM_ATTR_CONFIG.includes('variantInfo')) {
    const variantInfoEntries = Object.entries(variantInfo);
    variantInfoEntries.forEach(_ref3 => {
      var _exchangeTo$variantIn, _exchangeTo$variantIn2;
      let [key, value] = _ref3;
      const index = DISPLAY_ITEM_ATTR_CONFIG.indexOf(key);
      if (!(exchangeTo !== null && exchangeTo !== void 0 && exchangeTo.exchangeForCredit) && exchangeTo !== null && exchangeTo !== void 0 && (_exchangeTo$variantIn = exchangeTo.variantInfo) !== null && _exchangeTo$variantIn !== void 0 && _exchangeTo$variantIn[key] && (exchangeTo === null || exchangeTo === void 0 ? void 0 : (_exchangeTo$variantIn2 = exchangeTo.variantInfo) === null || _exchangeTo$variantIn2 === void 0 ? void 0 : _exchangeTo$variantIn2[key].value) !== value.value) {
        var _exchangeTo$variantIn3, _exchangeTo$variantIn4, _exchangeTo$variantIn5, _exchangeTo$variantIn6;
        attributes[index] = {
          key: `${value.optionName ?? value.optionId}: `,
          transaction: {
            before: value.name ?? value.value,
            after: (exchangeTo === null || exchangeTo === void 0 ? void 0 : (_exchangeTo$variantIn3 = exchangeTo.variantInfo) === null || _exchangeTo$variantIn3 === void 0 ? void 0 : (_exchangeTo$variantIn4 = _exchangeTo$variantIn3[key]) === null || _exchangeTo$variantIn4 === void 0 ? void 0 : _exchangeTo$variantIn4.name) ?? (exchangeTo === null || exchangeTo === void 0 ? void 0 : (_exchangeTo$variantIn5 = exchangeTo.variantInfo) === null || _exchangeTo$variantIn5 === void 0 ? void 0 : (_exchangeTo$variantIn6 = _exchangeTo$variantIn5[key]) === null || _exchangeTo$variantIn6 === void 0 ? void 0 : _exchangeTo$variantIn6.value)
          }
        };
      } else {
        attributes[index] = {
          key: `${value.optionName ?? value.optionId}: `,
          value: value.name ?? value.value
        };
      }

      // If color or size are included in the variant info, we track them in the provided booleans.
      if (key === 'color') colorIncludedInVariant = true;
      if (key === 'size') sizeIncludedInVariant = true;
    });
  }

  // COLOR IF NOT INCLUDED IN VARIANT INFO
  const colorIndex = DISPLAY_ITEM_ATTR_CONFIG.indexOf('color');
  if (!colorIncludedInVariant && item.color && colorIndex >= 0) {
    attributes[colorIndex] = {
      key: labels === null || labels === void 0 ? void 0 : labels.colorLabel,
      value: item.color
    };
  }

  // SIZE IF NOT INCLUDED IN VARIANT INFO
  const sizeIndex = DISPLAY_ITEM_ATTR_CONFIG.indexOf('size');
  if (!sizeIncludedInVariant && item.size && sizeIndex >= 0) {
    attributes[sizeIndex] = {
      key: labels === null || labels === void 0 ? void 0 : labels.sizeLabel,
      value: item.size
    };
  }

  // SKU
  const sku = (exchangeTo === null || exchangeTo === void 0 ? void 0 : exchangeTo.sku) ?? item.sku;
  const skuIndex = DISPLAY_ITEM_ATTR_CONFIG.indexOf('sku');
  if (sku && skuIndex >= 0) {
    attributes[skuIndex] = {
      key: labels === null || labels === void 0 ? void 0 : labels.skuLabel,
      value: sku
    };
  }

  // PRICE
  const priceIndex = DISPLAY_ITEM_ATTR_CONFIG.indexOf('price');
  if (priceIndex >= 0) {
    var _item$price;
    if (typeof ((_item$price = item.price) === null || _item$price === void 0 ? void 0 : _item$price.value) === 'number') {
      var _item$discountedPrice;
      let priceItem;
      if (!(exchangeTo !== null && exchangeTo !== void 0 && exchangeTo.exchangeForCredit) && typeof (exchangeTo === null || exchangeTo === void 0 ? void 0 : exchangeTo.price.value) === 'number' && exchangeTo.price.value !== item.price.value) {
        priceItem = {
          key: labels === null || labels === void 0 ? void 0 : labels.priceLabel,
          transaction: {
            before: /*#__PURE__*/React__default["default"].createElement(Currency.Currency, {
              locale: locale,
              includeCurrency: includeCurrency,
              value: item.price.value,
              currency: item.price.currency
            }),
            after: /*#__PURE__*/React__default["default"].createElement(Currency.Currency, {
              locale: locale,
              includeCurrency: includeCurrency,
              value: exchangeTo.price.value,
              currency: exchangeTo.price.currency
            })
          }
        };
      } else if ('discountedPrice' in item && typeof ((_item$discountedPrice = item.discountedPrice) === null || _item$discountedPrice === void 0 ? void 0 : _item$discountedPrice.value) === 'number') {
        priceItem = {
          key: labels === null || labels === void 0 ? void 0 : labels.priceLabel,
          transaction: {
            before: /*#__PURE__*/React__default["default"].createElement(Currency.Currency, {
              locale: locale,
              includeCurrency: includeCurrency,
              value: item.price.value,
              currency: item.price.currency
            }),
            after: /*#__PURE__*/React__default["default"].createElement(Currency.Currency, {
              locale: locale,
              includeCurrency: includeCurrency,
              value: item.discountedPrice.value,
              currency: item.discountedPrice.currency
            })
          }
        };
      } else {
        priceItem = {
          key: labels === null || labels === void 0 ? void 0 : labels.priceLabel,
          value: /*#__PURE__*/React__default["default"].createElement(Currency.Currency, {
            locale: locale,
            includeCurrency: includeCurrency,
            value: item.price.value,
            currency: item.price.currency
          })
        };
      }
      attributes[priceIndex] = priceItem;
    }
  }

  // ADDITIONAL ATTRIBUTES
  // additionalProperties are passed in via the item and need display.
  // NOTE: They will only be displayed if configured via itemDisplay.itemAttributeConfig in Quin.
  const additionalItemAttrs = getAdditionalItemAttrs(item.additionalProperties);
  if (additionalItemAttrs) {
    Object.keys(additionalItemAttrs).forEach(key => {
      const index = DISPLAY_ITEM_ATTR_CONFIG.indexOf(key);
      if (index >= 0) {
        var _thisAdditionalItemAt, _thisAdditionalItemAt2;
        // To find the current additional attribute, we need to find the object with the attribute key that matches the current key.
        const thisAdditionalItemAttributeConfig = additionalItemAttributeConfig === null || additionalItemAttributeConfig === void 0 ? void 0 : additionalItemAttributeConfig.find(item => item.attribute === key);

        // If there is a custom label in the this attribute config, we use it, otherwise we use the label from the additionalItemAttrs object which comes from the retailer.
        const label = (thisAdditionalItemAttributeConfig === null || thisAdditionalItemAttributeConfig === void 0 ? void 0 : (_thisAdditionalItemAt = thisAdditionalItemAttributeConfig.value) === null || _thisAdditionalItemAt === void 0 ? void 0 : _thisAdditionalItemAt.label) ?? additionalItemAttrs[key].label;

        // Then we create a key value pair for rendering the attribute.  If configured, we transform the value using getTransformedAttribute.
        attributes[index] = {
          key: `${label}: `,
          // We add colon and space to the label.
          value: getTransformedAttribute((thisAdditionalItemAttributeConfig === null || thisAdditionalItemAttributeConfig === void 0 ? void 0 : (_thisAdditionalItemAt2 = thisAdditionalItemAttributeConfig.value) === null || _thisAdditionalItemAt2 === void 0 ? void 0 : _thisAdditionalItemAt2.transform) ?? 'none', {
            key,
            additionalItemAttrs,
            additionalItemAttributeConfig,
            includeCurrency,
            locale
          })
        };
      }
    });
  }

  // Return the filtered attributes to remove any empty slots in the original attributes array.
  // Empty slots can occur if a given attributes doesn't pass a give check.  e.g. quantity is 0
  return attributes.filter(n => n != null);
};
const getItemProps = _ref4 => {
  var _exchangeTo$variantIn7, _exchangeTo$variantIn8;
  let {
    item,
    exchangeTo,
    isClaim
  } = _ref4;
  const returnType = isClaim ? 'claim' : exchangeTo && exchangeTo.exchangeForCredit ? 'exchangeForCredit' : exchangeTo ? 'exchange' : 'return';
  const title = returnType === 'return' ? item.name : (exchangeTo === null || exchangeTo === void 0 ? void 0 : exchangeTo.name) ?? (exchangeTo === null || exchangeTo === void 0 ? void 0 : (_exchangeTo$variantIn7 = exchangeTo.variantInfo) === null || _exchangeTo$variantIn7 === void 0 ? void 0 : (_exchangeTo$variantIn8 = _exchangeTo$variantIn7.product) === null || _exchangeTo$variantIn8 === void 0 ? void 0 : _exchangeTo$variantIn8.name) ?? item.name;
  const thumbnail = returnType === 'exchange' ? {
    old: item.imageUrl,
    new: exchangeTo === null || exchangeTo === void 0 ? void 0 : exchangeTo.imageUrl
  } : item.imageUrl;
  return {
    thumbnail,
    title,
    returnType
  };
};

exports.DEFAULT_ITEM_ATTR_CONFIG = DEFAULT_ITEM_ATTR_CONFIG;
exports.filterItems = filterItems;
exports.getAdditionalItemAttrs = getAdditionalItemAttrs;
exports.getItemAttrs = getItemAttrs;
exports.getItemProps = getItemProps;
exports.splitItemsForMixReturns = splitItemsForMixReturns;
